// contexts/CartContext.js
import React, { createContext, useState, useContext, useEffect } from "react";

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

const API = process.env.REACT_APP_API_BASE_URL;

const parseJsonSafe = (s) => {
  if (!s) return null;
  try {
    const o = JSON.parse(s);
    return typeof o === "object" && o ? o : null;
  } catch {
    return null;
  }
};

const mapDtoItem = (i) => ({
  id: i.id,
  productId: i.productId ?? null,
  serviceId: i.serviceId ?? null,
  quantity: Number(i.quantity ?? 1),
  priceSnapshot: Number(i.priceSnapshot ?? 0),
  realPrice: Number(i.realPrice),
  isWaitingForContact: i.isWaitingForContact === true,
  optionName: i.optionName ?? null,
  selectedOptions: i.selectedOptions ?? parseJsonSafe(i.optionsJson) ?? null,
  selectedDates: parseJsonSafe(i.selectedDatesJson) ?? null,
  title: i.title ?? "", // déjà FR/EN via API
  description: i.description ?? "",
  imageUrl: i.imageUrl ?? null,
  itemType: i.itemType ?? (i.productId ? "product" : "service"),
  linkedItemId: i.linkedItemId ?? i.productId ?? i.serviceId ?? null,
  // utilitaire
  totalPrice: Number(
    (Number(i.priceSnapshot ?? 0) * Number(i.quantity ?? 1)).toFixed(2)
  ),
});

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [userId, setUserId] = useState(null);

  const fetchOpenCart = async (uid) => {
    const res = await fetch(`${API}/api/Store/Cart?userId=${uid}`);
    if (!res.ok) return [];
    const data = await res.json(); // { id, userId, status, items:[...] }
    return Array.isArray(data.items) ? data.items.map(mapDtoItem) : [];
  };

  // Charger le panier au login
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const items = await fetchOpenCart(userId);
        setCart(items);
      } catch (err) {
        console.error("Erreur chargement panier :", err);
      }
    })();
  }, [userId]);

  const handleAddToCart = async (item) => {
    try {
      if (!userId) return;

      await fetch(`${API}/api/Store/Cart/Add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          productId: item.productId ?? null,
          serviceId: item.serviceId ?? null,
          quantity: Number(item.quantity ?? 1),
          priceSnapshot: Number(item.price ?? item.priceSnapshot ?? 0),
          realPrice: Number(item.realPrice),

          isWaitingForContact: !!item.isWaitingForContact, // avant: !!item.proposeDates
          optionName: item.OptionName ?? item.optionName ?? null,
          optionsJson: item.options ? JSON.stringify(item.options) : null,
          selectedDatesJson: item.selectedDates
            ? JSON.stringify(item.selectedDates)
            : null,

          eventDate: item.eventDate ?? null,
          startTime: item.startTime ?? null,
          endTime: item.endTime ?? null,
        }),
      });

      // rechargement pour récupérer title/description/imageUrl localisés
      const items = await fetchOpenCart(userId);
      setCart(items);
    } catch (err) {
      console.error("Erreur ajout panier :", err);
    }
  };

  const handleRemoveFromCart = async (index) => {
    const item = cart[index];
    setCart((prev) => prev.filter((_, i) => i !== index)); // optimiste

    try {
      if (!userId || !item?.id) return;
      await fetch(`${API}/api/Store/Cart/Item/${item.id}?userId=${userId}`, {
        method: "DELETE",
      });
      // optionnel: refetch pour être 100% sync
      const items = await fetchOpenCart(userId);
      setCart(items);
    } catch (err) {
      console.error("Erreur suppression panier :", err);
    }
  };

  const clearCart = async () => {
    setCart([]);
    try {
      if (!userId) return;
      await fetch(`${API}/api/Store/Cart/Clear?userId=${userId}`, {
        method: "DELETE",
      });
    } catch (err) {
      console.error("Erreur clear panier :", err);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        handleAddToCart,
        handleRemoveFromCart,
        clearCart,
        setUserId,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
